import { Typography } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import Content from '../components/Layout/Content';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import { Box } from '@mui/system';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import SearchBar from '../components/SearchBar/Search';
import NotFound from '../components/NotFound/NotFoundPage';
import colors from '../shared/colors.js';
import PaginationComponent from '../components/paginationComponent/ServerSidePaginationComponent';
import {
  handleTimeFilter,
  handleStatusFilter,
  searchDataByKey,
  proceduresArrayConvertor,
  extractValueFromCoding,
  getIconFromSubItemsArray,
  calculateDateFilterTimeStamp,
  getStatusFilterParamList,
} from '../shared/helper';
import {
  proceduresStatusFilterDatas,
  DEFAULT_NULL_REPLACEMENT,
  NUMBER_OF_ROWS_PER_PAGE,
  dateFilterMenuItems,
  PROCEDURES_STATUS_LIST,
} from '../shared/constants';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import { useSnackbar } from 'notistack';
import { useLazyQuery } from '@apollo/client';
import {
  FETCH_ALL_PROCEDURES_QUERY,
  FETCH_PROCEDURE_BY_ID,
} from '../services/ProceduresQueries/ProceduresService';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import ApplicationContext from '../context/applicationContext';
import {
  PROCEDURES,
  PROCEDURES_TABLE_HEADING as tableHeading,
  PROCEDURES_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};

const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};

export const Procedures = () => {
  const [openFlyout, setOpenFlyout] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [recordsData, setRecordedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailsData, setDetailsData] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [totalCount, setTotalCount] = useState();
  const [offset, setOffset] = useState(0);
  const [modalTitle, setModalTitle] = useState('');
  const { menuItems } = useContext(ApplicationContext);
  const { enqueueSnackbar } = useSnackbar();

  // const allProcedures = useQuery(FETCH_ALL_PROCEDURES_QUERY, {
  //   variables: {
  //     sourceUrl: '',
  //     count: '10',
  //     offset: 0,
  //     dateFilter: 0,
  //     statusFilter: '',
  //     searchFilter: '',
  //   },
  // });

  const [fetchAllPrecedures, fetchAllProceduresCall] = useLazyQuery(
    FETCH_ALL_PROCEDURES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(PROCEDURES.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        let result = res.fetchAllProcedures.procedureList;
        let convetor = JSON.parse(result);
        let data = proceduresArrayConvertor(convetor);
        setRecordedData(data);
        setFilteredData(data);
        setTotalCount(res.fetchAllProcedures.count);
      },
    }
  );

  const [fetchProcedureById, fetchProcedureByIdCall] = useLazyQuery(
    FETCH_PROCEDURE_BY_ID,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(PROCEDURES.ON_ERROR_TEXT.BY_ID, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchProcedureById);
      },
    }
  );

  useEffect(() => {
    fetchAllPrecedures({
      variables: {
        sourceUrl: '',
        count: NUMBER_OF_ROWS_PER_PAGE.toString(),
        offset: offset,
        dateFilter: calculateDateFilterTimeStamp(timeFilter) || 0,
        statusFilter: getStatusFilterParamList(
          statusFilter,
          proceduresStatusFilterDatas,
          PROCEDURES_STATUS_LIST
        ),
        searchFilter: searchValue,
      },
    });
  }, [offset, searchValue, timeFilter, statusFilter, fetchAllPrecedures]);

  // useEffect(() => {
  //   fetchAllPrecedures({
  //     variables: {
  //       sourceUrl: '',
  //       count: NUMBER_OF_ROWS_PER_PAGE.toString(),
  //       offset: offset,
  //       dateFilter: 0,
  //       statusFilter: '',
  //       searchFilter: '',
  //     },
  //   });
  // }, [offset]);
  const handleChangeFilter = () => {
    setOffset(0);
  };

  const onPageClick = pageNumber => {
    setOffset((pageNumber - 1) * 10);
  };

  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const onSearch = text => {
    setSearchValue(text);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const statusFilterOptions = {
    options: proceduresStatusFilterDatas,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const showFlyout = result => {
    const parseResult = JSON.parse(result?.procedure);
    const tableRowInfo = recordsData.find(obj => obj.id === selectedTableId);
    const modalData = [
      {
        column_name: PROCEDURES.MODAL_DATA.STATUS,
        value: tableRowInfo.status,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.RECORDED_ON,
        value: tableRowInfo.recordedOn,
        width: 6,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.FACILITY,
        value: parseResult?.location?.display || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.CODE,
        value: parseResult?.code?.coding?.[0]?.code || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.OUTCOME,
        value:
          extractValueFromCoding(parseResult?.outcome) ||
          DEFAULT_NULL_REPLACEMENT,
        show_status_icon: true,
        status_color: colors.GREEN,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.COMPLICATIONS,
        value:
          extractValueFromCoding(parseResult?.complication?.[0]) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.CATEGORY,
        value:
          extractValueFromCoding(parseResult?.category) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.FOLLOW_UP,
        value:
          extractValueFromCoding(parseResult?.followUp?.[0]) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.RECORDED_BY,
        value:
          parseResult?.recorder?.display ||
          parseResult?.performer?.[0]?.actor?.display ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.REASON_CODE,
        value:
          extractValueFromCoding(parseResult?.reasonCode?.[0]) ||
          DEFAULT_NULL_REPLACEMENT,
        width: 12,
      },
      {
        column_name: PROCEDURES.MODAL_DATA.BODY_SITE,
        value:
          extractValueFromCoding(parseResult?.bodySite?.[0]) ||
          DEFAULT_NULL_REPLACEMENT,
        width: 12,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  const viewDetailsHandler = id => {
    setSelectedTableId(id);
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTitle(tableRowInfo.procedure);
    fetchProcedureById({
      variables: {
        procedureId: id,
      },
    });
  };

  // React.useEffect(() => {
  //   const timeFilteredData = handleTimeFilter(
  //     timeFilter,
  //     recordsData,
  //     'recordedOn'
  //   );
  //   const statusFilteredData = handleStatusFilter(
  //     statusFilter,
  //     timeFilteredData,
  //     proceduresStatusFilterDatas
  //   );
  //   const searchData = searchDataByKey(
  //     statusFilteredData,
  //     'procedure',
  //     searchValue
  //   );
  //   setFilteredData(searchData);
  // }, [timeFilter, statusFilter, searchValue, recordsData]);
  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title">{PROCEDURES.TITLE_TEXT}</Typography>
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={PROCEDURES.SEARCH_PLACEHOLDER_TEXT}
          setOffset={setOffset}
        />
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
        <FilterDropDown {...statusFilterOptions} filterType="Status:" />
      </Box>

      <Box sx={table}>
        {!fetchAllProceduresCall.loading && !filteredData.length ? (
          <NotFound title={PROCEDURES.TITLE_TEXT} />
        ) : (
          <>
            <ReusableTable
              tableHeading={tableHeading}
              data={filteredData}
              viewDetailsHandler={viewDetailsHandler}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 18,
              }}>
              {totalCount > NUMBER_OF_ROWS_PER_PAGE && (
                <PaginationComponent
                  numberOfRowsPerPage={NUMBER_OF_ROWS_PER_PAGE}
                  count={totalCount}
                  onPageClick={onPageClick}
                  page={offset / 10}
                />
              )}
            </div>
          </>
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(
                menuItems?.Medical?.subItems,
                'Procedures'
              )
            : null
        }
        category={PROCEDURES.TITLE_TEXT}
        title={modalTitle}></ServiceFlyout>
      <SimpleBackdrop
        open={fetchAllProceduresCall.loading || fetchProcedureByIdCall.loading}
      />
    </Content>
  );
};
